import React, { useEffect, useRef, useState } from 'react';
import { loadYTApi } from '../utils/youtubeApi';

function YouTubePlayer({ videoId, width, height, x, y, isPlaying, onVideoClick, onSelect, isSelected, tool, currentTime, onSeek, onPlayPause }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  // Suppress onStateChange echo while we're applying a remote change
  const suppressRef = useRef(false);
  const [iframeActive, setIframeActive] = useState(false);

  const isDrawingTool = tool === 'pen' || tool === 'highlighter' || tool === 'eraser';

  // Attach YT IFrame API to the iframe element
  useEffect(() => {
    let destroyed = false;
    loadYTApi().then(() => {
      if (destroyed || !iframeRef.current) return;
      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onStateChange: (e) => {
            if (suppressRef.current) return;
            const S = window.YT.PlayerState;
            const t = playerRef.current?.getCurrentTime?.() ?? 0;
            if (e.data === S.PLAYING) {
              onPlayPause?.(true);
              onSeek?.(t);
            } else if (e.data === S.PAUSED || e.data === S.ENDED) {
              onPlayPause?.(false);
              onSeek?.(t);
            }
          },
        },
      });
    });
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy?.(); } catch (_) {}
      playerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Sync play/pause from remote
  useEffect(() => {
    const p = playerRef.current;
    if (!p?.playVideo) return;
    suppressRef.current = true;
    try {
      if (isPlaying) p.playVideo();
      else p.pauseVideo();
    } catch (_) {}
    const t = setTimeout(() => { suppressRef.current = false; }, 700);
    return () => clearTimeout(t);
  }, [isPlaying]);

  // Sync seek from remote (only jump if diff > 1s to avoid micro-jitter)
  useEffect(() => {
    if (currentTime === undefined) return;
    const p = playerRef.current;
    if (!p?.seekTo) return;
    const cur = p.getCurrentTime?.() ?? 0;
    if (Math.abs(cur - currentTime) > 1) {
      suppressRef.current = true;
      p.seekTo(currentTime, true);
      const t = setTimeout(() => { suppressRef.current = false; }, 700);
      return () => clearTimeout(t);
    }
  }, [currentTime]);

  const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&enablejsapi=1`;

  return (
    <foreignObject x={x} y={y} width={width} height={height}>
      {/* Explicit px, not '100%' — Safari doesn't reliably resolve
          percentage sizing against a <foreignObject>'s own coordinate box
          under a transformed/scaled ancestor (the whiteboard pans/zooms
          via the SVG's viewBox). Everything nested below (iframe, overlays)
          sizes off this div now, which Safari handles fine once it's a
          normal HTML box with a definite length instead of '100%'. */}
      <div style={{ width: `${width}px`, height: `${height}px`, position: 'relative' }}>
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title="YouTube video player"
          width="100%"
          height="100%"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'block', border: 'none' }}
        />

        {/* Not-yet-playing overlay: click to start */}
        {!isPlaying && !isDrawingTool && (
          <div
            style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              background: 'rgba(0,0,0,0.72)', borderRadius: 8, zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
            onClick={(e) => { e.stopPropagation(); onSelect?.(); onVideoClick?.(); }}
          >
            <div style={{ fontSize: 52, color: '#fff', lineHeight: 1 }}>▶</div>
          </div>
        )}

        {/* Playing overlay: blocks iframe for drag/select; double-click opens iframe briefly */}
        {isPlaying && !isDrawingTool && !iframeActive && (
          <div
            style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              zIndex: 10, cursor: 'pointer', background: 'transparent',
            }}
            onClick={(e) => { e.stopPropagation(); onSelect?.(); }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIframeActive(true);
              setTimeout(() => setIframeActive(false), 4000);
            }}
          />
        )}
      </div>
    </foreignObject>
  );
}

export default YouTubePlayer;
